import { format, formatDistanceToNow, differenceInSeconds } from 'date-fns';

export function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

export function formatDate(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return format(d, 'MMM d, yyyy HH:mm');
}

export function formatRelativeTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return formatDistanceToNow(d, { addSuffix: true });
}

// --- Kubernetes resource format helpers ---

/** Parse memory value (K8s string "4Gi" or bytes number) to GB */
export function parseMemoryGb(memory: string | number): number {
  if (typeof memory === 'number') {
    // Raw bytes from ManagementService
    return memory / (1024 * 1024 * 1024);
  }
  const str = String(memory);
  const match = str.match(/^(\d+(?:\.\d+)?)(Gi|Mi|Ki|Ti)?$/);
  if (!match) return 0;
  const value = parseFloat(match[1]);
  switch (match[2]) {
    case 'Ti': return value * 1024;
    case 'Gi': return value;
    case 'Mi': return value / 1024;
    case 'Ki': return value / (1024 * 1024);
    default:   return value / (1024 * 1024 * 1024);
  }
}

/** Parse Kubernetes memory string (e.g. "4Gi", "512Mi") or bytes number to display string */
export function formatMemorySize(k8sMemory: string | number): string {
  if (!k8sMemory && k8sMemory !== 0) return '-';

  if (typeof k8sMemory === 'number') {
    // Raw bytes from ManagementService
    const gb = k8sMemory / (1024 * 1024 * 1024);
    if (gb >= 1) return `${parseFloat(gb.toFixed(1))} GB`;
    const mb = k8sMemory / (1024 * 1024);
    return `${parseFloat(mb.toFixed(1))} MB`;
  }

  const match = k8sMemory.match(/^(\d+(?:\.\d+)?)(Gi|Mi|Ki|Ti)?$/);
  if (!match) return k8sMemory;

  const value = parseFloat(match[1]);
  const unit = match[2] || '';

  switch (unit) {
    case 'Ti': return `${value} TB`;
    case 'Gi': return `${value} GB`;
    case 'Mi': return `${value} MB`;
    case 'Ki': return `${value} KB`;
    default:   return `${value} bytes`;
  }
}

/** Parse CPU value (K8s string "4"/"500m" or millicores number 2000) to cores */
export function parseCpuCores(cpu: string | number): number {
  if (typeof cpu === 'number') {
    // Raw number from ManagementService: values >= 100 are millicores, < 100 are cores
    return cpu >= 100 ? cpu / 1000 : cpu;
  }
  const str = String(cpu);
  if (str.endsWith('m')) {
    return parseInt(str.slice(0, -1), 10) / 1000;
  }
  return parseFloat(str) || 0;
}

/** Parse Kubernetes CPU string (e.g. "4", "500m") or millicores number to display string */
export function formatProcessorCount(k8sCpu: string | number): string {
  if (!k8sCpu && k8sCpu !== 0) return '-';

  const cores = parseCpuCores(k8sCpu);
  if (cores < 1) {
    return `${Math.round(cores * 1000)}m`;
  }
  return cores === 1 ? '1 core' : `${cores} cores`;
}

/** Format shape as a compact string, e.g. "4 cores / 8 GB" */
export function formatShape(processorCount: string | number, memorySize: string | number): string {
  return `${formatProcessorCount(processorCount)} / ${formatMemorySize(memorySize)}`;
}

// --- TTL / Expiry helpers ---

/** Format seconds to human-readable duration (e.g. 3600 -> "1 hour") */
export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (minutes === 0) return hours === 1 ? '1 hour' : `${hours} hours`;
  return `${hours}h ${minutes}m`;
}

/** Calculate remaining time from an expiry date, returns null if expired */
export function getTimeRemaining(expiresAt: string | undefined): {
  seconds: number;
  display: string;
  isUrgent: boolean;
} | null {
  if (!expiresAt) return null;

  const now = new Date();
  const expiry = new Date(expiresAt);
  const remaining = differenceInSeconds(expiry, now);

  if (remaining <= 0) {
    return { seconds: 0, display: 'Expired', isUrgent: true };
  }

  return {
    seconds: remaining,
    display: formatDuration(remaining),
    isUrgent: remaining < 1800, // urgent if less than 30 minutes
  };
}

/** Format sandbox state for display */
export function formatState(state: string): string {
  return state;
}

/** Get CSS class for sandbox state badge */
export function getStateClass(state: string): string {
  switch (state) {
    case 'Running':    return 'status-running';
    case 'Creating':   return 'status-starting';
    case 'Deleting':   return 'status-stopping';
    case 'Terminated': return 'status-stopped';
    case 'Deleted':    return 'status-stopped';
    case 'Failed':     return 'status-error';
    default:           return 'status-stopped';
  }
}
